import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Download, Upload, Key, XCircle, RefreshCw, HelpCircle, ExternalLink, FileText, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useGuideStore } from '../../store/guideStore';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface Step {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  position: 'bottom' | 'left' | 'right' | 'top';
  elementId: string;
}

const steps: Step[] = [
  {
    id: 'getting-started',
    title: 'Premiers pas',
    description: 'Découvrez les étapes essentielles pour bien démarrer avec Shopiweb Manager. Apprenez à télécharger et installer le thème Shopiweb Premium, connecter votre boutique Shopify et activer votre licence en toute simplicité.',
    icon: HelpCircle,
    position: 'bottom',
    elementId: 'getting-started-section',
    titlepopup: 'Bien démarrer avec Shopiweb Manager'
  },
  {
    id: 'services',
    title: 'Services',
    description: 'Explorez les services exclusifs de Shopiweb Manager conçus pour optimiser la gestion de votre boutique. Profitez d\'outils avancés, d\'un accompagnement personnalisé et de solutions adaptées pour maximiser vos performances sur Shopify.',
    icon: ExternalLink,
    position: 'right',
    elementId: 'services-section',
    titlepopup: 'Explorer les services disponibles'
  },
  {
    id: 'news',
    title: 'Actualités',
    description: 'Ne manquez aucune nouveauté ! Retrouvez les dernières mises à jour, améliorations et nouvelles fonctionnalités de Shopiweb Manager pour tirer le meilleur parti de votre boutique Shopify.',
    icon: Sparkles,
    position: 'bottom',
    elementId: 'news-section',
    titlepopup: 'Suivre les dernières actualités'
  },
  {
    id: 'documentation',
    title: 'Documentation',
    description: 'Accédez à la documentation complète de Shopiweb et maîtrisez toutes les fonctionnalités du thème. Retrouvez des guides détaillés, des tutoriels et des astuces pour optimiser votre boutique Shopify.',
    icon: FileText,
    position: 'right',
    elementId: 'documentation-section',
    titlepopup: 'Accéder à la documentation complète'
  },
  {
    id: 'activate',
    title: 'Activer la licence',
    description: 'Débloquez toutes les fonctionnalités de Shopiweb Premium en activant votre licence. Profitez d\'un accès complet aux options de personnalisation, aux mises à jour et à l\'assistance dédiée.',
    icon: Key,
    position: 'bottom',
    elementId: 'activate-license',
    titlepopup: 'Activer votre licence Premium'
  },
  {
    id: 'install',
    title: 'Installer le thème',
    description: 'Installez automatiquement le thème Shopiweb Premium sur votre boutique Shopify grâce à Shopiweb Manager. Connectez votre boutique, puis lancez l’installation en un clic pour gagner du temps et profiter d’une configuration optimisée.',
    icon: Upload,
    position: 'right',
    elementId: 'install-theme',
    titlepopup: 'Installer votre thème'
  },
  {
    id: 'update',
    title: 'Mettre à jour le thème',
    description: 'Assurez-vous de toujours utiliser la dernière version du thème Shopiweb Premium pour profiter des nouvelles fonctionnalités, des améliorations de performance et des correctifs de sécurité. Gardez votre boutique à jour en un seul clic !',
    icon: RefreshCw,
    position: 'bottom',
    elementId: 'update-theme',
    titlepopup: 'Mettre à jour votre thème'
  },
  {
    id: 'download',
    title: 'Télécharger le thème',
    description: 'Téléchargez manuellement le thème Shopiweb Premium depuis votre espace client si vous préférez l’installation classique via un fichier ZIP. Une alternative utile en cas de besoin spécifique.',
    icon: Download,
    position: 'right',
    elementId: 'download-theme',
    titlepopup: 'Télécharger le thème manuellement'
  },
  {
    id: 'deactivate',
    title: 'Désactiver la licence',
    description: 'Libérez votre licence Shopiweb Premium en la désactivant afin de l\'utiliser sur une autre boutique. Suivez les étapes pour effectuer ce transfert en toute simplicité et sans interruption.',
    icon: XCircle,
    position: 'right',
    elementId: 'deactivate-license',
    titlepopup: 'Désactiver votre licence si besoin'
  }
];

const InteractiveGuide: React.FC = () => {
  const { isVisible, currentStep, setIsVisible, setCurrentStep } = useGuideStore();
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [hasAnnouncementBanner, setHasAnnouncementBanner] = useState(false);

  useEffect(() => {
    // Vérifier si la bannière d'annonce est visible
    const checkAnnouncementBanner = () => {
      setHasAnnouncementBanner(document.body.classList.contains('has-announcement-banner'));
    };
    
    checkAnnouncementBanner();
    
    // Écouter les changements de visibilité de la bannière
    const handleBannerChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setHasAnnouncementBanner(customEvent.detail.isVisible);
    };
    
    document.addEventListener('announcementBannerVisibilityChange', handleBannerChange);
    
    return () => {
      document.removeEventListener('announcementBannerVisibilityChange', handleBannerChange);
    };
  }, []);

  useEffect(() => {
    const hasCompleted = localStorage.getItem('tutorialCompleted');
    const hasSeenIntro = localStorage.getItem('tutorialIntroSeen');
    const lastPostponeTime = localStorage.getItem('tutorialPostponedAt');
    setHasCompletedTutorial(!!hasCompleted);
    
    // Vérifier si le guide a été reporté et si 5 minutes se sont écoulées
    if (!hasCompleted && !isMobile && !hasSeenIntro) {
      if (lastPostponeTime) {
        const postponeTime = parseInt(lastPostponeTime);
        const fiveMinutesLater = postponeTime + (5 * 60 * 1000); // 5 minutes en millisecondes
        
        if (Date.now() >= fiveMinutesLater) {
          setShowIntro(true);
          localStorage.removeItem('tutorialPostponedAt'); // Réinitialiser le report
        }
      } else {
        setShowIntro(true);
      }
    }
  }, [setIsVisible, isMobile]);

  useEffect(() => {
    // Hide guide when route changes
    setIsVisible(false);
  }, [location, setIsVisible]);

  // Hide guide on mobile
  if (isMobile) {
    return null;
  }

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const handleSkip = () => {
    completeTutorial();
  };

  const completeTutorial = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    setHasCompletedTutorial(true);
    setIsVisible(false);
  };

  const startTutorial = () => {
    localStorage.setItem('tutorialIntroSeen', 'true');
    localStorage.removeItem('tutorialPostponedAt');
    setShowIntro(false);
    setIsVisible(true);
    setCurrentStep(0);
  };

  const postponeTutorial = () => {
    // Sauvegarder le moment où l'utilisateur a reporté le guide
    localStorage.setItem('tutorialPostponedAt', Date.now().toString());
    setShowIntro(false);
  };

  const getStepPosition = (position: string, element: HTMLElement | null) => {
    if (!element) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const rect = element.getBoundingClientRect();
    const margin = 20;
    const popupWidth = 300;
    const popupHeight = 200;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Ajustement pour la bannière d'annonce (48px de hauteur)
    const bannerOffset = hasAnnouncementBanner ? 48 : 0;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'bottom':
        top = rect.bottom + margin;
        left = rect.left + (rect.width / 2) - (popupWidth / 2);
        left = Math.max(margin, Math.min(left, windowWidth - popupWidth - margin));
        break;
      case 'left':
        top = rect.top + (rect.height / 2) - (popupHeight / 2);
        left = rect.left - popupWidth - margin;
        if (left < margin) {
          left = rect.right + margin;
        }
        break;
      case 'right':
        top = rect.top + (rect.height / 2) - (popupHeight / 2);
        left = rect.right + margin;
        if (left + popupWidth > windowWidth - margin) {
          left = rect.left - popupWidth - margin;
        }
        break;
      case 'top':
        top = rect.top - popupHeight - margin;
        left = rect.left + (rect.width / 2) - (popupWidth / 2);
        if (top < margin + bannerOffset) {
          top = rect.bottom + margin;
        }
        break;
    }

    top = Math.max(margin + bannerOffset, Math.min(top, windowHeight - popupHeight - margin));

    return { top, left };
  };

  // Render intro modal
  if (showIntro) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[999]">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="relative p-6 bg-blue-600 text-white">
            <div className="flex items-center gap-3">
              <HelpCircle className="w-8 h-8" />
              <h3 className="text-xl font-semibold">Bienvenue sur Shopiweb Manager 👋</h3>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Souhaitez-vous découvrir les fonctionnalités clés de Shopiweb Manager grâce à un guide interactif ?
            </p>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              En quelques étapes, vous apprendrez à :
            </p>

            <ul className="space-y-3 mb-8">
              {steps.map((step) => (
                <li key={step.id} className="flex items-center gap-3">
                  <step.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-gray-700 dark:text-gray-200">{step.titlepopup}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={startTutorial}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-base font-medium shadow-md"
              >
                🚀 Commencer le guide
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={postponeTutorial}
                className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-base font-medium"
              >
                ⏱ Plus tard
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!isVisible) {
    return null;
  }

  const currentStepData = steps[currentStep];
  const targetElement = document.getElementById(currentStepData.elementId);
  const position = getStepPosition(currentStepData.position, targetElement);

  return (
    <>
      {/* Semi-transparent overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[30]" />

      {/* Guide Popup */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          style={{
            top: position.top,
            left: position.left,
            position: 'fixed',
            zIndex: 1001
          }}
          className="w-[300px] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="relative p-4 bg-blue-600 text-white">
            <button
              onClick={handleSkip}
              className="absolute top-2 right-2 p-1 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <currentStepData.icon className="w-6 h-6" />
              <h3 className="text-lg font-semibold">{currentStepData.title}</h3>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {currentStepData.description}
            </p>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentStep
                      ? 'bg-blue-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
              >
                Passer
              </button>
              <button
                onClick={handleNext}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-md"
              >
                {currentStep === steps.length - 1 ? 'Terminer' : 'Suivant'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Highlight current element */}
      {targetElement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed z-[999] pointer-events-none"
          style={{
            top: targetElement.getBoundingClientRect().top + window.scrollY - 4,
            left: targetElement.getBoundingClientRect().left - 4,
            width: targetElement.offsetWidth + 8,
            height: targetElement.offsetHeight + 8,
          }}
        >
          {/* Dark overlay around element */}
          <div 
            className="absolute inset-0"
            style={{
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
              borderRadius: '12px'
            }}
          />
          
          {/* Animated border */}
          <div 
            className="absolute inset-0 border-2 border-blue-500 rounded-xl"
            style={{
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          />
          
          {/* Glow effect */}
          <div 
            className="absolute inset-0 bg-blue-500/10 rounded-xl"
            style={{
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          />
        </motion.div>
      )}
    </>
  );
};

export default InteractiveGuide;